import {
    ArrowPathIcon,
    MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import {
    Button,
    SelectItem,
    Tooltip,
} from "@heroui/react";
import React, { memo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { formatEnumString, formatSportName, formatWeightClassName } from "../../utils/utilMethods";
import AppSelect from "../../components/common/appSelect";

interface FeedsFiltersProps {
    searchQuery: string;
    setSearchQuery: (query: string) => void;
    selectedSport: string;
    setSelectedSport: (sport: string) => void;
    selectedWeight: string;
    setSelectedWeight: (weight: string) => void;
    selectedManagerType: string;
    setSelectedManagerType: (type: string) => void;
    selectedProfileType: string;
    setSelectedProfileType: (type: string) => void;
    sports: string[]; 
    weightClasses: string[];
    loadSportsIfNeeded: () => Promise<void>;
    loadWeightClassesIfNeeded: () => Promise<void>;
    resetFilters: () => void;
    activeTab: "all" | "my";
}

const FeedsFiltersComponent: React.FC<FeedsFiltersProps> = ({
    searchQuery,
    setSearchQuery,
    selectedSport,
    setSelectedSport,
    selectedWeight,
    setSelectedWeight,
    selectedManagerType,
    setSelectedManagerType,
    selectedProfileType,
    setSelectedProfileType,
    sports,
    weightClasses,
    loadSportsIfNeeded,
    loadWeightClassesIfNeeded,
    resetFilters,
    activeTab,
}) => {
    const { t } = useTranslation("feeds");
    const managerTypeOptions = ["INDIVIDUAL", "AGENCY"];

    const handleSearchChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            setSearchQuery(e.target.value);
        },
        [setSearchQuery]
    );

    const handleSportChange = useCallback(
        (key: any) => {
            setSelectedSport(Array.from(key)[0] as string);
        },
        [setSelectedSport]
    );

    const handleWeightChange = useCallback(
        (key: any) => {
            setSelectedWeight(Array.from(key)[0] as string);
        },
        [setSelectedWeight]
    );

    const handleManagerTypeChange = useCallback(
        (key: any) => {
            setSelectedManagerType(Array.from(key)[0] as string);
        },
        [setSelectedManagerType]
    );

    const handleSportOpenChange = useCallback(
        (isOpen: boolean) => {
            if (isOpen) {
                loadSportsIfNeeded();
            }
        },
        [loadSportsIfNeeded]
    );

    const handleWeightOpenChange = useCallback(
        (isOpen: boolean) => {
            if (isOpen) {
                loadWeightClassesIfNeeded();
            }
        },
        [loadWeightClassesIfNeeded]
    );

    return (
        <div className="flex items-center gap-2 flex-wrap">
            <div className="relative w-full sm:w-72 shrink-0">
                <input
                    type="text"
                    placeholder={t("search_by_title", "Search by title")}
                    value={searchQuery}
                    onChange={handleSearchChange}
                    className="w-full h-9 pl-9 pr-3 py-2 bg-white rounded-xl text-sm font-normal font-['Plus_Jakarta_Sans'] leading-5 text-gray-700 placeholder:text-gray-400 outline outline-1 outline-offset-[-1px] outline-gray-200 focus:outline-blue-400 transition-all"
                />
                <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 absolute left-3 top-[10px] pointer-events-none" />
            </div>

            {activeTab === "all" && (
                <>
                    <AppSelect
                        placeholder={t("select_sport", "Select sport")}
                        selectedKeys={selectedSport ? [selectedSport] : []}
                        onSelectionChange={handleSportChange}
                        onOpenChange={handleSportOpenChange}
                        variant="bordered"
                        classNames={{
                            base: "shrink-0 w-64",
                            trigger: "h-9 min-h-[36px] rounded-xl border-gray-200 shadow-none px-3 data-[open=true]:border-blue-400 bg-white",
                            value: "text-sm font-normal font-['Plus_Jakarta_Sans'] leading-5 text-gray-500",
                            selectorIcon: "text-gray-400",
                        }}
                        size="sm"
                        aria-label={t("select_sport", "Select sport")}
                    >
                        {sports.length > 0 ? (
                            sports.map((sport) => (
                                <SelectItem key={sport}>{formatSportName(sport)}</SelectItem>
                            ))
                        ) : (
                            <SelectItem key="no-sport" isDisabled>
                                {t("no_sports_available", "No sports available")}
                            </SelectItem>
                        )}
                    </AppSelect>
                    <AppSelect
                        placeholder={t("select_weight_class", "Select weight")}
                        selectedKeys={selectedWeight ? [selectedWeight] : []}
                        onSelectionChange={handleWeightChange}
                        onOpenChange={handleWeightOpenChange}
                        variant="bordered"
                        classNames={{
                            base: "shrink-0 w-48",
                            trigger: "h-9 min-h-[36px] rounded-xl border-gray-200 shadow-none px-3 data-[open=true]:border-blue-400 bg-white",
                            value: "text-sm font-normal font-['Plus_Jakarta_Sans'] leading-5 text-gray-500",
                            selectorIcon: "text-gray-400",
                        }}
                        size="sm"
                        aria-label={t("select_weight_class", "Select weight")}
                    >
                        {weightClasses.length > 0 ? (
                            weightClasses.map((weight) => (
                                <SelectItem key={weight}>{formatWeightClassName(weight)}</SelectItem>
                            ))
                        ) : (
                            <SelectItem key="no-weight" isDisabled>
                                {t("no_weight_classes_available", "No weight classes available")}
                            </SelectItem>
                        )}
                    </AppSelect>
                    <AppSelect
                        placeholder={t("select_manager_type", "Select man")}
                        selectedKeys={selectedManagerType ? [selectedManagerType] : []}
                        onSelectionChange={handleManagerTypeChange}
                        variant="bordered"
                        classNames={{
                            base: "shrink-0 w-48",
                            trigger: "h-9 min-h-[36px] rounded-xl border-gray-200 shadow-none px-3 data-[open=true]:border-blue-400 bg-white",
                            value: "text-sm font-normal font-['Plus_Jakarta_Sans'] leading-5 text-gray-500",
                            selectorIcon: "text-gray-400",
                        }}
                        size="sm"
                        aria-label={t("select_manager_type", "Select manager")}
                    >
                        {managerTypeOptions.length > 0 ? (
                            managerTypeOptions.map((type) => (
                                <SelectItem key={type}>{formatEnumString(type)}</SelectItem>
                            ))
                        ) : (
                            <SelectItem key="no-manager-type" isDisabled>
                                {t("no_manager_types_available", "No manager types available")}
                            </SelectItem>
                        )}
                    </AppSelect>
                </>
            )}

            <Tooltip content={t("reset_filters", "Reset filters")}>
                <Button
                    isIconOnly
                    variant="flat"
                    onClick={resetFilters}
                    className="h-9 w-9 min-w-[36px] rounded-xl bg-gray-100 hover:bg-gray-200/80 text-gray-500 hover:text-gray-800 shrink-0"
                    size="sm"
                    aria-label={t("reset_filters", "Reset filters")}
                >
                    <ArrowPathIcon className="w-4 h-4" />
                </Button>
            </Tooltip>
        </div>
    );
};

export const FeedsFilters = memo(FeedsFiltersComponent);